"use client";

import { useRef } from "react";
import { useInstantSearch } from "react-instantsearch";
import { DropdownSection } from "~/features/algolia/components/AlgoliaFacetFilters/components/DropdownSection";
import { ToggleSection } from "~/features/algolia/components/AlgoliaFacetFilters/components/ToggleSection";
import { camelCaseToReadable } from "~/lib/utils/camelCaseToReadable";

// Attributes rendered as an on/off switch instead of a checkbox dropdown.
const TOGGLE_ATTRIBUTES = ["isFreeRecipe", "isOutlet", "isArchive"] as const;
export type ToggleAttribute = (typeof TOGGLE_ATTRIBUTES)[number];

const isToggleAttribute = (attribute: string): attribute is ToggleAttribute =>
	(TOGGLE_ATTRIBUTES as readonly string[]).includes(attribute);

const FACET_TITLES: Record<string, string> = {
	accessoryCategory: "Kategori",
	accessoryNeedleSize: "Nålstørrelse",
	isArchive: "Arkiv",
	isFreeRecipe: "Gratis oppskrifter",
	isOutlet: "Outlet",
	knittingDirection: "Strikkeretning",
	knittingGauge: "Strikkefasthet",
	needleCategory: "Pinnetype",
	needleSize: "Pinnenummer",
	productType: "Produkttype",
	recipeDifficulty: "Vanskelighetsgrad",
	recipeFits: "Passer til",
	recipeGarmentType: "Type plagg",
	recipeLanguage: "Språk",
	recipeTechniques: "Teknikk",
	recipeYarnType: "Garn",
	yarnType: "Garntype",
	theme: "Tema",
	yoke: "Type bærestykke",
	shoulderType: "Type skulder",
	structure: "Struktur",
	formFit: "Passform",
	neck: "Hals",
};

const getFacetTitle = (attribute: string) =>
	FACET_TITLES[attribute] ?? camelCaseToReadable(attribute);

const FacetFilter = ({ attribute }: { attribute: string }) => {
	const title = getFacetTitle(attribute);

	if (isToggleAttribute(attribute)) {
		return <ToggleSection attribute={attribute} title={title} />;
	}

	return <DropdownSection attribute={attribute} limit={100} title={title} />;
};

export const AlgoliaFacetFilters = () => {
	const { results } = useInstantSearch();

	// Facet order comes from the page's Algolia rule (renderingContent).
	// The searchbar disables rules while a query is active (enableRules={!query}),
	// which strips renderingContent — so latch the last known order and keep
	// showing the same facets during search.
	const lastOrder = useRef<string[]>([]);
	const liveOrder = results?.renderingContent?.facetOrdering?.facets?.order;
	if (liveOrder?.length) {
		lastOrder.current = liveOrder;
	}
	const order = liveOrder?.length ? liveOrder : lastOrder.current;

	return (
		<>
			{order.map((attribute) => (
				<FacetFilter key={attribute} attribute={attribute} />
			))}
		</>
	);
};
